import { useEffect, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';

import Button from 'components/field/Button';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OTO_LINK } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { formatPrice } from 'utils/utils';

import { FormHeader } from '../OTOLinkBuyer';
import * as S from './Tabs.styled';

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const SelectShippingMethodTab = ({ nextStep, goToContactDetailsTab, prevStep }) => {
	const { showError } = useAlert();
	const [dcLoading, setDCLoading] = useState(true);

	const formMethods = useFormContext();
	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isSubmitting, isDirty, isValid }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OTO_LINK.UPDATE_OTO_LINK_BUYER_SHIPPING_METHOD, {
				key: data.key,
				GUID: data.GUID,
				otoLinkSessionId: data.otoLinkSessionId,
				selectedShippingOptionId: data.selectedDCSettingsId
			});
			if (response.status !== 200) {
				throw new Error(response);
			}
			reset(data);
			nextStep();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const getDCPrice = async () => {
		try {
			setDCLoading(true);
			const { data, status } = await openAxios.post(OTO_LINK.GET_OTO_LINK_BUYER_DC_PRICES, {
				key: watch('key'),
				GUID: watch('GUID'),
				otoLinkSessionId: watch('otoLinkSessionId')
			});
			if (status !== 200) {
				throw new Error(data);
			}
			reset({
				...watch(),
				deliveryCompanyList: data
			});
		} catch (error) {
			if (error?.response?.data?.code === 'error.L017') {
				AppLogger.error(error);
				return;
			}
			AppLogger.error(error);
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
		} finally {
			setDCLoading(false);
		}
	};

	useEffect(() => {
		if (watch('deliveryCompanyList')?.length > 0) {
			setDCLoading(false);
		} else {
			getDCPrice();
		}
	}, []);

	const selectDC = (dcSettingsId, price) => {
		setValue('selectedDCSettingsId', dcSettingsId, validateOptions);
		setValue('shippingFee', price, validateOptions);
		setValue('grandTotal', price + watch('subTotal'), validateOptions);
	};

	useEffect(() => {
		const selectedDC = watch('deliveryCompanyList')?.find(
			(dc) => dc.deliveryCompanySettingsId === watch('selectedDCSettingsId')
		);
		if (selectedDC) {
			selectDC(selectedDC?.deliveryCompanySettingsId, selectedDC?.priceWithVat);
		}
	}, [watch('deliveryCompanyList')]);

	if (!dcLoading && !watch('deliveryCompanyList')?.length) {
		return (
			<FormProvider {...formMethods}>
				<S.Card padding={0} background border={0} shadowed rounded>
					<S.Wrapper>
						<FormHeader
							title="selectShippingMethod"
							className="section-padding-bottom"
							onClick={() => prevStep()}
						/>
						<Typography className="text-center" fontWeight="500">
							otoLinkNoShippingMethodsFound
						</Typography>

						<S.FormRow id="confirm-form">
							<Button
								label="changeAddress"
								variant="blue"
								fullWidth
								size="xx-large"
								onClick={() => goToContactDetailsTab()}
							/>
						</S.FormRow>
					</S.Wrapper>
				</S.Card>
			</FormProvider>
		);
	}

	return (
		<FormProvider {...formMethods}>
			<S.Card padding={0} background border={0} shadowed rounded>
				<S.Wrapper>
					<div>
						<FormHeader
							title="selectShippingMethod"
							className="section-padding-bottom"
							onClick={() => prevStep()}
						/>

						<S.ShippingMethodWrapper>
							{dcLoading
								? Array.from({ length: 3 }).map((_, index) => (
										<Skeleton key={index} width="100%" height="15rem" />
									))
								: watch('deliveryCompanyList')?.map((dc) => (
										<SingleDeliveryCompany
											key={dc.deliveryCompanySettingsId}
											deliveryCompanySettingsId={dc.deliveryCompanySettingsId}
											deliveryTime={dc.deliveryTime}
											currency={dc?.currency || watch('currency')}
											logo={dc?.logo}
											name={dc?.shippingContract?.name}
											serviceType={dc?.serviceType}
											onSelectDC={(dcSettingsId, price) => selectDC(dcSettingsId, price)}
											pickupType={dc?.pickupType}
											price={dc?.priceWithVat}
											selected={watch('selectedDCSettingsId') === dc.deliveryCompanySettingsId}
										/>
									))}
						</S.ShippingMethodWrapper>
					</div>

					<S.FormRow id="confirm-form">
						<Button
							label="continue"
							variant="blue"
							fullWidth
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							disabled={isValid ? false : !isDirty}
							loading={isSubmitting || dcLoading}
							size="xx-large"
						/>
					</S.FormRow>
				</S.Wrapper>
			</S.Card>
		</FormProvider>
	);
};

export const SingleDeliveryCompany = ({
	deliveryCompanySettingsId,
	deliveryTime,
	currency,
	logo,
	name,
	serviceType,
	pickupType,
	price,
	selected,
	disabled,
	onSelectDC
}) => (
	<S.ShippingMethodCard
		className={selected && 'selected'}
		disabled={disabled}
		onClick={() => onSelectDC(deliveryCompanySettingsId, price)}>
		<S.ShippingMethodCardBadge>
			<Typography variant="caption" color="var(--white)" fontWeight="500">
				{deliveryTime}
			</Typography>
		</S.ShippingMethodCardBadge>
		<S.ShippingMethodCardContainer>
			<div>
				<S.TitleContainer>
					<S.DCLogo url={logo} width="5rem" height="5rem" />
					<S.DCInfo>
						<Typography variant="p3" fontWeight="500">
							{name}
						</Typography>
						<S.DCServiceType>
							<Typography variant="caption" fontWeight="500" color="var(--content-3)">
								{serviceType}
							</Typography>
						</S.DCServiceType>
					</S.DCInfo>
				</S.TitleContainer>
				<S.DCDropOffOptionContainer>
					<Typography variant="caption" fontWeight="500" color="var(--black-40)">
						dropOffOption
					</Typography>
					<Typography variant="p3" fontWeight="500">
						{pickupType}
					</Typography>
				</S.DCDropOffOptionContainer>
			</div>
			<Typography variant="p1" fontWeight="500">
				<Trans
					i18nKey="otoLinkTotalAmount"
					values={{
						value: formatPrice(price, currency),
						currency
					}}
					components={{
						blackText: <S.Bold />
					}}
				/>
			</Typography>
		</S.ShippingMethodCardContainer>
	</S.ShippingMethodCard>
);

export default SelectShippingMethodTab;
